// Room amenities — small icon strip for each room/suite card.
//
// Strategy: rooms in the data layer rarely carry a structured `amenities`
// array; instead, each language's `desc` describes the amenities in prose.
// This helper inspects the descriptions for well-known keywords (in EN /
// FR / ES) and returns the list of detected amenity ids.
//
// Rooms can also opt-in to an explicit list via `room.amenities = ["wifi",
// "ac", ...]` — that overrides keyword detection when present.

import {
  Wifi,
  Snowflake,
  Refrigerator,
  Tv,
  Bath,
  ShowerHead,
  Wind,
  Lock,
} from "lucide-react";

export const ROOM_AMENITIES = [
  {
    id: "minibar",
    icon: Refrigerator,
    label: { en: "Minibar", fr: "Minibar", es: "Minibar" },
    keywords: ["mini-bar", "minibar"],
  },
  {
    id: "shower",
    icon: ShowerHead,
    label: { en: "Shower", fr: "Douche", es: "Ducha" },
    keywords: ["shower", "douche", "ducha"],
  },
  {
    id: "bathroom",
    icon: Bath,
    label: { en: "Bathroom", fr: "Salle de bain", es: "Baño" },
    keywords: ["bathroom", "salle de bain", "baño", "bano"],
  },
  {
    id: "hairdryer",
    icon: Wind,
    label: { en: "Hairdryer", fr: "Sèche-cheveux", es: "Secador" },
    keywords: ["hair-dryer", "hairdryer", "hair dryer", "sèche-cheveux", "seche-cheveux", "sèche cheveux", "secador"],
  },
  {
    id: "ac",
    icon: Snowflake,
    label: { en: "Air conditioning", fr: "Climatisation", es: "Aire acondicionado" },
    keywords: ["air-conditioning", "air conditioning", "ac ", " a/c", "climatisation", "climatización", "climatizacion", "aire acondicionado"],
  },
  {
    id: "wifi",
    icon: Wifi,
    label: { en: "Wi-Fi", fr: "Wi-Fi", es: "Wi-Fi" },
    keywords: ["wi-fi", "wifi", "wi fi"],
  },
  {
    id: "safe",
    icon: Lock,
    label: { en: "Safe", fr: "Coffre-fort", es: "Caja fuerte" },
    keywords: ["safe-box", "safebox", "in-room safe", " safe ", "coffre-fort", "coffre fort", " coffre ", "caja fuerte"],
  },
  {
    id: "tv",
    icon: Tv,
    label: { en: "TV", fr: "TV", es: "TV" },
    keywords: ["satellite tv", "satellite-tv", "tv satellite", "tv satélite", "tv satelite", "smart tv", "led tv", "flat-screen", " tv ", "télévision", "television", "televisor"],
  },
];

const norm = (s) =>
  (typeof s === "string" ? s : "")
    .toLowerCase()
    // pad with leading/trailing spaces so the " tv " / " safe " token tests
    // match whether they appear at the start or end of the description.
    .replace(/[.,;:!?()]/g, " ");

export const inferRoomAmenities = (room) => {
  if (!room) return [];

  // Explicit override
  if (Array.isArray(room.amenities) && room.amenities.length) {
    const ids = new Set(room.amenities.map((a) => String(a).toLowerCase()));
    return ROOM_AMENITIES.filter((a) => ids.has(a.id));
  }

  // Keyword detection across all languages of the room description.
  const descObj = room.desc || {};
  const haystack = [" ", descObj.en, descObj.fr, descObj.es, " "]
    .filter(Boolean)
    .map(norm)
    .join(" | ");

  return ROOM_AMENITIES.filter((a) =>
    a.keywords.some((kw) => haystack.includes(kw))
  );
};
